import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { apiPost } from '../utils/api';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { buildViewSuffix } from '../constants/pageViewParams';

const DEBOUNCE_MS = 200;

// Paths we never want to log:
//   - admin/analytics: an admin reading the feed shouldn't pollute it
//   - /login etc: pre-auth routes have no user identity
const SKIP_PREFIXES = ['/admin/analytics', '/login', '/forgot-password', '/reset-password', '/accept-invite'];

function shouldSkip(path) {
  if (!path) return true;
  for (const p of SKIP_PREFIXES) {
    if (path === p || path.startsWith(p + '/')) return true;
  }
  return false;
}

/**
 * Log a page-view event to the server whenever the route changes.
 *
 * Fires a fire-and-forget POST /api/analytics/page-view with the new path.
 * Debounced so rapid back-button mashing collapses to one event. Skips
 * admin/analytics (avoids self-pollution) and pre-auth routes.
 *
 * ⚠️ "MOUNT THIS ONCE AT THE TOP OF THE AUTHENTICATED LAYOUT" WAS THE WHOLE
 * INSTRUCTION UNTIL 2026-09-16 AND IT LEFT TWO AUTHENTICATED PAGES DARK.
 * `components/Layout.js` calls it, but `App.js` renders `/budget/compare` (the
 * chrome-less variance pop-out) and `/onboarding/new-production` (the full-screen
 * setup wizard) OUTSIDE the Layout wrapper on purpose. Neither is in
 * SKIP_PREFIXES, so neither was skipped — they were simply never seen. Both now
 * call this hook themselves. **A route rendered outside `Layout` must either
 * call this hook or be declared in `client/src/App.pageViewCoverage.test.js`,
 * which fails CI otherwise.** Mounting it once ABOVE the router instead was
 * considered and rejected: it would fire on every pre-auth route, where the
 * server answers 204 and writes nothing, i.e. a request per navigation for no
 * row.
 *
 * ─── TWO DELIBERATE NON-BEHAVIOURS, BOTH DECIDED RATHER THAN INHERITED ───────
 *
 * 🟢 A REVISIT IS LOGGED. `lastLoggedRef` holds the LAST path logged, not a set
 * of every path ever seen, so A → B → A writes three rows. It exists only to
 * stop a repeated effect run on an UNCHANGED pathname writing twice (React 18
 * StrictMode double-invokes effects in development). This is written down
 * because a task brief asserted the opposite — "a user bouncing A→B→A logs A
 * once ever" — and a plausible-sounding claim about a ref is exactly the thing
 * someone will "fix" into a real defect.
 *
 * 🔀 A VIEW CHANGE *IS* LOGGED SINCE 2026-09-17, AND THIS DOCBLOCK USED TO SAY
 * THE OPPOSITE — read the reversal, do not restore the old paragraph.
 * It said: *"a tab click is not a page — logging one would multiply Department
 * Orders into a row per tab per filter combination"*, and concluded that if
 * per-tab usage were ever wanted *"it belongs in a field of its own, never as a
 * second page path."* The owner then asked for exactly this: *"I would like page
 * visits to also be included in activity so I can tell if they are visiting
 * which pagges, which tabs etc."*
 *
 * 🔑 THE OLD OBJECTION IS ANSWERED RATHER THAN OVERRULED, AND THAT IS WHY THE
 * SUFFIX IS A QUERY STRING. `helpers/analyticsEndpointMap.js#pathPartOf` cuts a
 * PAGE row at the first space and then at `?`, so `mapEndpointToPage`,
 * `mapEndpointToFeature` and `normaliseEndpointForGrouping` all still see a bare
 * `/department/orders`: **`page-popularity` and `feature-adoption` do not
 * fragment**, and only the per-user timeline — the surface that asked for it —
 * reads the tab. The fear was real; storing the view in the path part rather
 * than as a second page is what disarms it.
 *
 * 🔴 STILL NOT LOGGED, AND STILL DELIBERATELY: any search param that is not in
 * `constants/pageViewParams.js`'s allowlist. A search keystroke, a page number,
 * `?order=` / `?gag=` / `?teamId=` (record deep links, stripped from the URL by
 * `hooks/useDeepLinkParam.js`, so logging them writes TWO rows per link and
 * neither is a view) and `?token=` (a secret) all write nothing. Every one of
 * them is declared with its reason in that file rather than merely absent.
 *
 * ⚠️ The old paragraph's stated cost is unchanged: time spent inside one page is
 * still invisible. A user who opens Department Orders and works there all day
 * now logs one row per TAB they visit, not one row per hour.
 */
export default function usePageViewLogger() {
  const location = useLocation();
  const { publishedView } = usePageHeader();
  const timerRef = useRef(null);
  const lastLoggedRef = useRef(null);

  const path = location.pathname;
  // 🪤 A view published by the page we have just LEFT must not be pinned onto
  // the page we have just arrived at — see `usePageView`'s docblock. The
  // pathname travels with the value precisely so this comparison can exist.
  const viewId = publishedView && publishedView.path === path ? publishedView.viewId : null;
  const search = location.search;

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (shouldSkip(path)) return undefined;
    // 🔑 The KEY is what the no-duplicate-consecutive guard compares, and it is
    // the whole stored path including the view — so a tab click logs, and a
    // repeat of the same view (a re-render, StrictMode's double effect, a
    // search keystroke that changes no allowlisted param) does not.
    const key = path + buildViewSuffix(new URLSearchParams(search), viewId);
    if (lastLoggedRef.current === key) return undefined;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      lastLoggedRef.current = key;
      // Fire-and-forget; never block render or surface errors to the user.
      // 🔻 `title` IS NO LONGER SENT (2026-09-17, review M5.9). The server stored
      // it as `"<path> <title>"` and nothing ever read it back — and it could not
      // have said anything, because nothing in this app assigns `document.title`,
      // so all 358 dev PAGE rows carried the identical `"Crew Forge"`. The
      // endpoint still ACCEPTS the field, so an older client is unaffected.
      // 📐 The screen size (owner, 2026-10-06): the viewport in CSS px, read at
      // SEND time so it is the size the view was actually seen at. The server
      // validates it as a pair and stores NULL for anything not a sane integer
      // (`server/helpers/viewportSize.js`) — this is never trusted.
      apiPost('/api/analytics/page-view', {
        path: key,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
      }).catch(() => {});
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [path, search, viewId]);
}
